/** How the sidebar and /boards arrange boards: starred, recent, folders, the rest. Pure. */

export interface TreeBoard {
  id: string
  name: string
  starred: boolean
  lastOpenedAt: string | null
}

export interface TreeFolder {
  id: string
  name: string
  scope: 'personal' | 'team'
  boardIds: string[]
}

export interface FolderNode<B> {
  folder: TreeFolder
  boards: B[]
}

export interface BoardTree<B> {
  starred: B[]
  recent: B[]
  team: FolderNode<B>[]
  personal: FolderNode<B>[]
  /** Boards in none of my folders nor my team's. */
  unfiled: B[]
}

/** How many recently opened boards the tree lists. */
export const RECENT_LIMIT = 5

const byName = <B extends TreeBoard>(a: B, b: B) => a.name.localeCompare(b.name)

/**
 * The tree over the boards I can see. Folders keep their own order and list
 * only boards that are in `boards` (a board I lost access to, or archived,
 * drops out). Starred boards also stay in their folders.
 */
export function boardTree<B extends TreeBoard>(
  boards: B[],
  personal: TreeFolder[],
  team: TreeFolder[],
  recentLimit = RECENT_LIMIT,
): BoardTree<B> {
  const byId = new Map(boards.map((b) => [b.id, b]))
  const node = (folder: TreeFolder): FolderNode<B> => ({
    folder,
    boards: folder.boardIds.map((id) => byId.get(id)).filter((b): b is B => !!b),
  })
  const filed = new Set([...personal, ...team].flatMap((f) => f.boardIds))
  return {
    starred: boards.filter((b) => b.starred).sort(byName),
    recent: boards
      .filter((b) => b.lastOpenedAt)
      .sort((a, b) => b.lastOpenedAt!.localeCompare(a.lastOpenedAt!))
      .slice(0, recentLimit),
    team: team.map(node),
    personal: personal.map(node),
    unfiled: boards.filter((b) => !filed.has(b.id)).sort(byName),
  }
}

/** The folder holding the board in this list of folders (one scope), if any. */
export function folderOf(folders: TreeFolder[], boardId: string): TreeFolder | null {
  return folders.find((f) => f.boardIds.includes(boardId)) ?? null
}

/**
 * The PUT that files a board in `target` (appended at the end) or, for null,
 * takes it out of its current folder in `folders`. The API moves a board out
 * of sibling folders itself, so one write is enough. Null when nothing changes.
 */
export function placeBoard(
  folders: TreeFolder[],
  boardId: string,
  target: string | null,
): { folderId: string; boardIds: string[] } | null {
  const current = folderOf(folders, boardId)
  if (target === null) {
    if (!current) return null
    return { folderId: current.id, boardIds: current.boardIds.filter((id) => id !== boardId) }
  }
  if (current?.id === target) return null
  const folder = folders.find((f) => f.id === target)
  if (!folder) return null
  return { folderId: folder.id, boardIds: [...folder.boardIds, boardId] }
}

/** Case-insensitive name match; everything for a blank query. */
export function matchBoards<B extends { name: string }>(boards: B[], q: string): B[] {
  const term = q.trim().toLowerCase()
  return term ? boards.filter((b) => b.name.toLowerCase().includes(term)) : boards
}
